import { useState, useEffect, useMemo } from 'react';
import { 
    fetchRecurringPayments, 
    tokenizeCard, 
    createRecurringPlan, 
    chargeInitialPayment,
    cancelCharge
} from '../services/recurringService';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';

export const useRecurringPayments = () => {
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [showModal, setShowModal] = useState(false);
    
    // Estados para cancelación
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [cancelLoading, setCancelLoading] = useState(false);

    const handleCancelClick = (payment) => {
        setSelectedPayment(payment);
        setShowCancelModal(true);
    };

    const handleCancelConfirm = async (password) => {
        // COMENTADO POR SOLICITUD - VALIDACIÓN DE CONTRASEÑA EN CANCELACIÓN
        /*
        const storedPassword = localStorage.getItem('login_password');
        if (password !== storedPassword) {
            toast.error("La contraseña ingresada es incorrecta.");
            return;
        }
        */

        setCancelLoading(true);
        try {
            await cancelCharge(selectedPayment.id);
            toast.success("Pago recurrente cancelado con éxito.");
            setShowCancelModal(false);
            await loadPayments(currentPage);
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || "Error al cancelar el pago recurrente.";
            toast.error(errorMessage);
            console.error(err);
        } finally {
            setCancelLoading(false);
        }
    };
    
    // Estados de paginación
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [limit] = useState(20);

    // Fetch inicial y por cambio de página
    useEffect(() => {
        loadPayments(currentPage);
    }, [currentPage]);

    const loadPayments = async (page = currentPage) => {
        setLoading(true);
        try {
            const response = await fetchRecurringPayments(page, limit);
            const rawPayments = response?.data || [];
            const pagination = response?.pagination || {};
            
            const frequencyMap = {
                weekly: 'Semanal',
                biweekly: 'Quincenal',
                monthly: 'Mensual'
            };

            const normalized = rawPayments.map(p => ({
                id: p.id,
                customerName: p.customerName || p.customer_name || `${p.firstName || ''} ${p.lastName || ''}`.trim() || 'Cliente Recurrente',
                email: p.email || p.customer_email || 'N/A',
                concept: p.concept || p.description || 'Sin concepto',
                amount: parseFloat(p.amount) || 0,
                currency: p.currency || 'MXN',
                frequency: frequencyMap[p.periodicity] || p.frequency || 'Mensual',
                status: p.status === 'ACTIVE' ? 'Activo' : (p.status === 'CANCELLED' ? 'Cancelado' : p.status),
                nextPayment: p.next_payment_date || p.nextPayment || p.start_date,
                createdAt: p.created_at || p.createdAt || p.start_date || new Date().toISOString().split('T')[0]
            }));

            setPayments(normalized);
            setTotalPages(pagination.total_pages || 1);
            setTotalCount(pagination.total_count || rawPayments.length);
        } catch (err) {
            setError("Error al cargar los pagos recurrentes.");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    // Lógica de filtrado
    const filteredPayments = useMemo(() => {
        return payments.filter(p => {
            const customerName = p.customerName || '';
            const email = p.email || '';
            const id = p.id || '';
            const concept = p.concept || '';
            
            const matchesSearch = customerName.toLowerCase().includes(searchTerm.toLowerCase()) || 
                                email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                concept.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesStatus = statusFilter ? p.status === statusFilter : true;
            return matchesSearch && matchesStatus;
        });
    }, [payments, searchTerm, statusFilter]);

    /**
     * Creación de Plan Recurrente Unificado:
     * Envía directamente los datos de tarjeta, cliente y configuración del plan
     * al endpoint unificado en una sola petición.
     */
    const handleCreatePayment = async (formData) => {
        setLoading(true);
        try {
            const planData = {
                pan: formData.pan,
                expMonth: formData.expMonth,
                expYear: formData.expYear,
                cvv: formData.cvv || "",
                holderName: formData.holderName,
                customerInformation: {
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    email: formData.email,
                    address1: formData.address1,
                    city: formData.city,
                    country: formData.country,
                    ip: "127.0.0.1"
                },
                amount: parseFloat(formData.amount),
                currency: String(formData.currency),
                chargeDay: parseInt(formData.chargeDay) || 1,
                chargeNumber: parseInt(formData.chargeNumber) || 1,
                startDate: "today",
                periodicity: formData.periodicity,
                initialAmount: parseFloat(formData.initialAmount) || 0,
                description: formData.concept,
                customer_name: `${formData.firstName} ${formData.lastName}`.trim()
            };
            
            const planResponse = await createRecurringPlan(planData);
            if (!planResponse || planResponse.status !== true) {
                throw new Error("Error al crear el plan recurrente.");
            }

            Swal.fire({
                icon: 'success',
                title: '¡Plan Recurrente Creado con Éxito!',
                html: `
                    <div style="text-align: left; font-family: Montserrat, sans-serif; font-size: 14px;">
                        <p><strong>ID del Plan:</strong> <code style="font-size: 12px;">${planResponse.id}</code></p>
                        <hr />
                        <p><strong>Concepto:</strong> ${formData.concept}</p>
                        <p><strong>Monto Total:</strong> $${parseFloat(formData.amount).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${formData.currency === '484' ? 'MXN' : 'USD'}</p>
                        <p><strong>Frecuencia:</strong> ${formData.periodicity === 'monthly' ? 'Mensual' : (formData.periodicity === 'weekly' ? 'Semanal' : 'Quincenal')}</p>
                        <p><strong>Número de Cargos:</strong> ${planResponse.chargeNumber || formData.chargeNumber}</p>
                        ${planResponse.initialPaymentAmount ? `
                            <hr />
                            <p style="color: #0E9600; font-weight: bold; margin-bottom: 5px;"><strong>Anticipo Cobrado:</strong></p>
                            <p><strong>Monto del Anticipo:</strong> $${parseFloat(planResponse.initialPaymentAmount).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                            <p><strong>Estado del Pago:</strong> <span class="badge bg-success" style="padding: 4px 10px; font-size: 11px;">${planResponse.initialPaymentStatus}</span></p>
                            <p><strong>ID de Solicitud de Pago:</strong> <code style="font-size: 11px;">${planResponse.initialPaymentRequestId}</code></p>
                        ` : ''}
                    </div>
                `,
                confirmButtonColor: '#0E9600',
                confirmButtonText: 'Entendido'
            });
            
            /* Actualizar lista local basándose en la respuesta simplificada de la API
            const newPayment = {
                id: planResponse.id,
                concept: formData.concept,
                customerName: `${formData.firstName} ${formData.lastName}`,
                email: formData.email,
                amount: parseFloat(formData.amount),
                currency: formData.currency === '484' ? 'MXN' : 'USD',
                frequency: formData.periodicity === 'monthly' ? 'Mensual' : (formData.periodicity === 'weekly' ? 'Semanal' : 'Quincenal'),
                status: "Activo",
                nextPayment: formData.startDate || new Date().toISOString().split('T')[0],
                createdAt: new Date().toISOString().split('T')[0]
            };

            setPayments(prev => [newPayment, ...prev]); */
            if (currentPage === 1) {
                await loadPayments(1);
            } else {
                setCurrentPage(1);
            }
            setShowModal(false);
            return { success: true };

        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || "Error en el proceso.";
            toast.error(errorMessage);
            console.error(err);
            return { success: false, message: errorMessage };
        } finally {
            setLoading(false);
        }
    };

    return {
        payments: filteredPayments,
        loading,
        error,
        searchTerm,
        setSearchTerm,
        statusFilter,
        setStatusFilter,
        showModal,
        setShowModal,
        handleCreatePayment,
        reload: () => loadPayments(currentPage),
        currentPage,
        totalPages,
        totalCount,
        handlePageChange,
        
        // Retorno de cancelación
        showCancelModal,
        setShowCancelModal,
        selectedPayment,
        cancelLoading,
        handleCancelClick,
        handleCancelConfirm
    };
};
